import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, Save, Lock, RefreshCw } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('auth_token') || '';

function FeatureEditor({ value, onChange }) {
  return (
    <div>
      <textarea
        rows={Math.max(4, value.length + 1)}
        className="ch-admin-textarea"
        value={value.join('\n')}
        onChange={e => onChange(e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
        placeholder="One feature per line"
        data-testid="perks-editor"
      />
      <p style={{ fontSize: 11, color: 'var(--op-muted)', marginTop: 4 }}>One feature per line · {value.length} feature{value.length !== 1 ? 's' : ''}</p>
    </div>
  );
}

function PlanEditor({ plan, onSave }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...plan });
  const [saving, setSaving] = useState(false);

  const f = key => e => setForm(prev => ({ ...prev, [key]: e.target.value }));
  const fNum = key => e => setForm(prev => ({ ...prev, [key]: parseInt(e.target.value, 10) || 0 }));
  const fBool = key => e => setForm(prev => ({ ...prev, [key]: e.target.checked }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(plan.key, {
        label: form.label,
        badge_label: form.badge_label || null,
        price: form.price,
        price_label: form.price_label,
        post_count: form.post_count,
        duration_days: form.duration_days,
        is_popular: form.is_popular,
        is_gated: form.is_gated,
        note: form.note || null,
        perks: form.perks,
        is_active: form.is_active,
      });
      setEditing(false);
      toast.success(`${plan.label} plan updated.`);
    } catch {
      toast.error('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => { setForm({ ...plan }); setEditing(false); };

  return (
    <div className={`plan-edit-card${editing ? ' plan-edit-card--active' : ''}`} data-testid={`plan-edit-${plan.key}`}>
      {/* Header */}
      <div className="plan-edit-card__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            className="plan-edit-key-chip"
            style={{ background: plan.accent_color, color: '#fff' }}
          >
            {plan.key}
          </span>
          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--op-ink)', fontFamily: "'Inter Tight', sans-serif" }}>
            {plan.label}
          </span>
          {plan.is_popular && <span className="plan-edit-badge plan-edit-badge--popular">Popular</span>}
          {plan.is_gated && <span className="plan-edit-badge plan-edit-badge--gated"><Lock size={10} /> Gated</span>}
          {!plan.is_active && <span className="plan-edit-badge plan-edit-badge--inactive">Inactive</span>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {editing ? (
            <>
              <button
                className="plan-edit-btn plan-edit-btn--primary"
                onClick={handleSave}
                disabled={saving}
                data-testid={`save-plan-${plan.key}`}
              >
                {saving ? <RefreshCw size={13} className="spinning" /> : <Save size={13} />}
                {saving ? ' Saving…' : ' Save Changes'}
              </button>
              <button className="plan-edit-btn" onClick={cancel}>Cancel</button>
            </>
          ) : (
            <button
              className="plan-edit-btn plan-edit-btn--primary"
              onClick={() => setEditing(true)}
              data-testid={`edit-plan-${plan.key}`}
            >
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Fields */}
      {editing ? (
        <div className="plan-edit-fields">
          <div className="plan-edit-grid-3">
            <div className="plan-edit-field">
              <label className="plan-edit-label">Plan Name</label>
              <input className="plan-edit-input" value={form.label} onChange={f('label')} data-testid="edit-label" />
            </div>
            <div className="plan-edit-field">
              <label className="plan-edit-label">Badge Label <span className="plan-edit-hint">(leave blank for none)</span></label>
              <input className="plan-edit-input" value={form.badge_label || ''} onChange={f('badge_label')} data-testid="edit-badge" />
            </div>
            <div className="plan-edit-field">
              <label className="plan-edit-label">Price (USD)</label>
              <input type="number" min={0} className="plan-edit-input" value={form.price} onChange={fNum('price')} data-testid="edit-price" />
            </div>
            <div className="plan-edit-field">
              <label className="plan-edit-label">Price Label <span className="plan-edit-hint">(displayed string)</span></label>
              <input className="plan-edit-input" value={form.price_label} onChange={f('price_label')} data-testid="edit-price-label" />
            </div>
            <div className="plan-edit-field">
              <label className="plan-edit-label">Post Count</label>
              <input type="number" min={1} className="plan-edit-input" value={form.post_count} onChange={fNum('post_count')} data-testid="edit-post-count" />
            </div>
            <div className="plan-edit-field">
              <label className="plan-edit-label">Duration (days)</label>
              <input type="number" min={1} className="plan-edit-input" value={form.duration_days} onChange={fNum('duration_days')} data-testid="edit-duration" />
            </div>
          </div>
          <div className="plan-edit-grid-3" style={{ marginTop: 12 }}>
            <div className="plan-edit-field">
              <label className="plan-edit-label">Admin Note <span className="plan-edit-hint">(shown on locked tier)</span></label>
              <input className="plan-edit-input" value={form.note || ''} onChange={f('note')} data-testid="edit-note" />
            </div>
          </div>
          <div className="plan-edit-toggles">
            <label className="plan-edit-toggle-opt">
              <input type="checkbox" checked={form.is_popular} onChange={fBool('is_popular')} data-testid="toggle-popular" />
              Mark as Popular
            </label>
            <label className="plan-edit-toggle-opt">
              <input type="checkbox" checked={form.is_gated} onChange={fBool('is_gated')} data-testid="toggle-gated" />
              <Lock size={12} /> Gated (not publicly selectable)
            </label>
            <label className="plan-edit-toggle-opt">
              <input type="checkbox" checked={form.is_active} onChange={fBool('is_active')} data-testid="toggle-active" />
              Active (visible to employers)
            </label>
          </div>
          <div className="plan-edit-field" style={{ marginTop: 14 }}>
            <label className="plan-edit-label">Features List <span className="plan-edit-hint">(one per line)</span></label>
            <FeatureEditor value={form.perks} onChange={perks => setForm(prev => ({ ...prev, perks }))} />
          </div>
        </div>
      ) : (
        <div className="plan-edit-preview">
          <div className="plan-edit-preview-row">
            <span className="plan-edit-preview-stat"><strong>{form.price_label}</strong></span>
            <span className="plan-edit-preview-stat">{form.post_count} post{form.post_count > 1 ? 's' : ''}</span>
            <span className="plan-edit-preview-stat">{form.duration_days} days</span>
          </div>
          <ul className="plan-edit-preview-perks">
            {form.perks.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function AdminJobPlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPlans = async () => {
    try {
      const res = await axios.get(`${API}/admin/job-plans`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setPlans(res.data);
    } catch {
      toast.error('Could not load job plans.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPlans(); }, []);

  const handleSave = async (key, update) => {
    await axios.put(`${API}/admin/job-plans/${key}`, update, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    await loadPlans();
  };

  return (
    <AdminLayout title="Career Hub" breadcrumb="Job Plans">
    <div style={{ maxWidth: 960 }} data-testid="admin-job-plans-page">
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#081D30', fontFamily: "'Inter Tight', sans-serif", margin: '0 0 6px' }} data-testid="admin-plans-heading">
          Career Hub — Listing Plans
        </h1>
        <p style={{ fontSize: 13, color: 'var(--op-muted)', margin: 0 }}>
          Edit tier names, prices, feature lists, and access rules. Changes take effect immediately on the Post a Listing page.
        </p>
      </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--op-muted)' }} data-testid="plans-loading">
            Loading plans…
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {plans.map(plan => (
              <PlanEditor key={plan.key} plan={plan} onSave={handleSave} />
            ))}
          </div>
        )}
      </div>

      <style>{`
        .plan-edit-card {
          background: #fff; border: 1px solid var(--op-border);
          border-radius: 14px; overflow: hidden;
          transition: border-color .15s;
        }
        .plan-edit-card--active { border-color: var(--op-teal); }
        .plan-edit-card__head {
          display: flex; align-items: center; justify-content: space-between;
          padding: 18px 24px; background: #fafaf8;
          border-bottom: 1px solid var(--op-border);
        }
        .plan-edit-key-chip {
          padding: 3px 10px; border-radius: 20px;
          font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: .08em;
        }
        .plan-edit-badge {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 2px 8px; border-radius: 20px; font-size: 11px; font-weight: 700;
          text-transform: uppercase; letter-spacing: .06em;
        }
        .plan-edit-badge--popular { background: rgba(0,140,134,.1); color: var(--op-teal); }
        .plan-edit-badge--gated { background: rgba(102,112,109,.1); color: var(--op-muted); }
        .plan-edit-badge--inactive { background: rgba(192,57,43,.1); color: #c0392b; }
        .plan-edit-btn {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 7px 14px; border-radius: 8px; border: 1px solid var(--op-border);
          font-size: 13px; font-weight: 600; cursor: pointer;
          background: #fff; color: var(--op-ink); transition: border-color .15s;
        }
        .plan-edit-btn--primary {
          background: var(--op-navy); color: #fff; border-color: var(--op-navy);
        }
        .plan-edit-btn--primary:disabled { opacity: .6; cursor: not-allowed; }
        .plan-edit-fields { padding: 20px 24px; display: flex; flex-direction: column; gap: 0; }
        .plan-edit-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
        @media (max-width: 700px) { .plan-edit-grid-3 { grid-template-columns: 1fr; } }
        .plan-edit-field { display: flex; flex-direction: column; gap: 6px; }
        .plan-edit-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: var(--op-ink); }
        .plan-edit-hint { font-weight: 400; text-transform: none; letter-spacing: 0; font-size: 10px; color: var(--op-muted); }
        .plan-edit-input {
          padding: 9px 12px; border: 1px solid var(--op-border); border-radius: 8px;
          background: #fafaf8; font-size: 13px; color: var(--op-ink); outline: none;
          font-family: inherit;
        }
        .plan-edit-input:focus { border-color: var(--op-teal); }
        .ch-admin-textarea {
          width: 100%; padding: 9px 12px; border: 1px solid var(--op-border); border-radius: 8px;
          background: #fafaf8; font-size: 12px; color: var(--op-ink); outline: none;
          font-family: monospace; resize: vertical; box-sizing: border-box;
        }
        .ch-admin-textarea:focus { border-color: var(--op-teal); }
        .plan-edit-toggles { display: flex; flex-wrap: wrap; gap: 16px; margin-top: 14px; }
        .plan-edit-toggle-opt {
          display: flex; align-items: center; gap: 7px;
          font-size: 13px; color: var(--op-ink); cursor: pointer;
        }
        .plan-edit-toggle-opt input { accent-color: var(--op-teal); }
        .plan-edit-preview { padding: 16px 24px; }
        .plan-edit-preview-row { display: flex; gap: 20px; margin-bottom: 10px; }
        .plan-edit-preview-stat { font-size: 14px; color: var(--op-ink); font-weight: 600; }
        .plan-edit-preview-perks { margin: 0; padding: 0 0 0 18px; }
        .plan-edit-preview-perks li { font-size: 13px; color: var(--op-muted); margin-bottom: 4px; }
        .spinning { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </AdminLayout>
  );
}
